import { invokeEdgeOrPresent } from '../shared/supabase/invokeEdgeOrPresent';
import type { FiscalProfileInput } from '../fiscal/domain/fiscalTypes';
import type { SaveFiscalProfileOptions } from '../fiscal/infra/fiscalRepository';
import { saveFiscalProfileRemote } from '../fiscal/infra/fiscalRepository';

export type IntegrationSecretsPayload = {
  mp_access_token?: string | null;
  /** OAuth refresh_token (opcional); requiere MP_OAUTH_CLIENT_ID/SECRET en el proyecto para renovación automática. */
  mp_refresh_token?: string | null;
  mp_webhook_secret?: string | null;
  arca_api_key?: string | null;
  arca_cert_base64?: string | null;
  arca_cert_pem?: string | null;
  arca_private_key_pem?: string | null;
};

export type IntegrationSecretsSaveResult = {
  ok: boolean;
  organization_id: string;
};

export const saveIntegrationSecrets = async (
  branchId: string,
  payload: IntegrationSecretsPayload
): Promise<IntegrationSecretsSaveResult> => {
  const body: Record<string, unknown> = { branch_id: branchId };

  // Claves opcionales: solo las incluimos si el caller las pasó explícitamente.
  if (payload.mp_access_token !== undefined) body.mp_access_token = payload.mp_access_token;
  if (payload.mp_refresh_token !== undefined) body.mp_refresh_token = payload.mp_refresh_token;
  if (payload.mp_webhook_secret !== undefined) body.mp_webhook_secret = payload.mp_webhook_secret;
  if (payload.arca_api_key !== undefined) body.arca_api_key = payload.arca_api_key;
  if (payload.arca_cert_base64 !== undefined) body.arca_cert_base64 = payload.arca_cert_base64;
  if (payload.arca_cert_pem !== undefined) body.arca_cert_pem = payload.arca_cert_pem;
  if (payload.arca_private_key_pem !== undefined) body.arca_private_key_pem = payload.arca_private_key_pem;

  const { data, error, presented } = await invokeEdgeOrPresent('integration-secrets-upsert', { body }, {
    module: 'settings',
    op: 'integration.secrets_upsert',
    fallbackMessage: 'Error al guardar secrets',
  });

  if (error) {
    if (presented) {
      throw error instanceof Error ? error : new Error(error.message || 'Error al guardar secrets');
    }
    throw new Error(error.message || 'Error al guardar secrets');
  }

  return data as IntegrationSecretsSaveResult;
};

/** Persiste perfil fiscal ARCA (régimen, CUIT emisor, PV, ambiente). */
export const saveFiscalProfile = async (
  branchId: string,
  profile: FiscalProfileInput,
  options?: SaveFiscalProfileOptions
): Promise<{ ok: boolean; organizationId: string }> => {
  return saveFiscalProfileRemote(branchId, profile, options);
};
